import type { ReactNode } from 'react';
import { CategoryTile } from '@/components/CategoryTile';
import type { CategoryMeta } from '@/components/transactions/screen/categoryIndex';
import {
  TransactionAmount,
  TransactionCategoryLabel,
  TransactionLine,
  TransactionMeta,
  TransactionMetaItem,
  TransactionTitle,
} from '@/components/transactions/TransactionLine';
import { SettlementAvatar } from './SettlementParts';

// On a phone each meta item has its own line, so the payer's share wraps rather than lose its end to an ellipsis; from
// `sm:` the items share the one dotted line and truncate like the personal row's items.
const META_TEXT = 'min-w-0 break-words sm:truncate';

// The list and the send flow's "what the others see" preview both render this, so the preview is literally the row
// the others get.
export function SpaceExpenseLine({ category, name, pills, payer, payerPhotoUrl, share, amountCents, sub, trailing, density, className }: Readonly<{
  category: CategoryMeta;
  name: string;
  pills?: ReactNode;
  payer: string;
  payerPhotoUrl?: string | null;
  /** Appended to "X pagou" — the viewer's part, when the split gives them one. */
  share?: ReactNode;
  amountCents: number;
  /** Under the amount: the purchase's total when only part of it entered. */
  sub?: ReactNode;
  trailing?: ReactNode;
  /** `compact` inside a padded card (the home), the full-bleed `row` in a list card (Despesas). */
  density?: 'row' | 'compact';
  className?: string;
}>) {
  const meta = density === 'compact'
    ? <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-2xs text-text-secondary"><SettlementAvatar name={payer} photoUrl={payerPhotoUrl} className="size-4 text-3xs" /><span className="min-w-0 truncate">{payer} pagou · {category.label}</span></p>
    : null;
  return <TransactionLine density={density} className={className} leading={<CategoryTile meta={category} size={density === 'compact' ? 'sm' : undefined} />}
    title={<TransactionTitle adornments={pills}>{name}</TransactionTitle>}
    meta={meta ?? <TransactionMeta>
      <TransactionMetaItem><TransactionCategoryLabel meta={category} /></TransactionMetaItem>
      <TransactionMetaItem separated><SettlementAvatar name={payer} photoUrl={payerPhotoUrl} className="size-4 text-3xs" /><span className={META_TEXT}>{payer} pagou{share}</span></TransactionMetaItem>
    </TransactionMeta>}
    amount={<TransactionAmount value={amountCents / 100} tone="neutral" sub={sub} />}
    trailing={trailing} />;
}
